'use client';

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFinancialStore } from '@/store/useFinancialStore';
import GlassCard from '@/components/GlassCard';
import GlassButton from '@/components/GlassButton';
import BrokerBridge from '@/components/BrokerBridge';
import { motion } from 'framer-motion';

export default function ProfilePage() {
  const { user } = useFinancialStore();
  const [isEditing, setIsEditing] = useState(false);

  const securityLogs = [
    { event: 'Key Rotation', location: 'New York, US', status: 'Success', time: '2h ago' },
    { event: 'IP Auth', location: 'London, UK', status: 'Verified', time: '12h ago' },
    { event: 'API Secret Accessed', location: 'New York, US', status: 'Encrypted', time: '1d ago' },
  ];

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary selection:bg-brand-teal/30 pb-20">
      {/* Header */}
      <header className="bg-bg-secondary/40 backdrop-blur-2xl border-b border-white/5 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex justify-between items-center">
            <div className="flex items-center gap-6">
              <Link to="/dashboard" className="p-3 bg-white/5 rounded-2xl hover:bg-white/10 transition-all border border-white/5 group">
                <svg className="w-5 h-5 text-text-tertiary group-hover:text-brand-teal-light transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div className="space-y-0.5">
                <h1 className="text-sm font-black tracking-[0.3em] text-text-tertiary uppercase">Identity Module</h1>
                <p className="text-xl font-bold tracking-tight text-text-primary">Operative <span className="text-brand-teal-light">Dossier</span></p>
              </div>
            </div>
            <GlassButton 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsEditing(!isEditing)} 
              className={`rounded-xl px-6 h-12 uppercase tracking-widest font-black text-[10px] ${isEditing ? 'bg-brand-teal/20 text-brand-teal-light' : 'text-text-tertiary'}`}
            >
              {isEditing ? 'Lock Configuration' : 'Mod Identity'}
            </GlassButton>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Identity Card */}
          <motion.div 
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="lg:col-span-1"
          >
            <GlassCard variant="glow" className="p-10 text-center relative overflow-hidden h-full">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-teal to-transparent shadow-glow" />
              <div className="w-32 h-32 mx-auto rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center text-5xl mb-8 relative group overflow-hidden">
                <div className="absolute inset-0 bg-brand-teal/5 rounded-3xl animate-pulse group-hover:bg-brand-teal/10 transition-all" />
                <span className="relative z-10 group-hover:scale-110 transition-transform duration-700">👤</span>
              </div>
              <h2 className="text-3xl font-black tracking-tight text-text-primary mb-2 uppercase">{user?.username || 'John Doe'}</h2>
              <div className="inline-block px-4 py-1.5 bg-brand-teal/10 border border-brand-teal/20 rounded-full text-[10px] font-black tracking-[0.2em] text-brand-teal-light mb-10">
                RANK: SOVEREIGN ELITE
              </div>
              
              <div className="space-y-4 pt-10 border-t border-white/5">
                {[
                  { label: 'Neural Uptime', value: '1,248 HRS' },
                  { label: 'Security Tier', value: 'ULTRA' },
                  { label: 'Identity Status', value: 'VERIFIED' }
                ].map((stat) => (
                  <div key={stat.label} className="flex justify-between items-center bg-white/[0.02] p-4 rounded-2xl border border-white/5">
                    <span className="text-[10px] font-black uppercase tracking-widest text-text-tertiary">{stat.label}</span>
                    <span className="text-xs font-mono font-black text-brand-teal-light">{stat.value}</span>
                  </div>
                ))}
              </div>
            </GlassCard>
          </motion.div>

          {/* Configuration Sections */}
          <div className="lg:col-span-2 space-y-8">
            {/* Account Settings */}
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}>
              <GlassCard variant="elevated" className="p-10 border-white/5">
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-text-tertiary mb-10">Core Identity Fields</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-text-tertiary uppercase tracking-[0.2em] pl-1">Handle</label>
                    <input 
                      readOnly={!isEditing}
                      type="text" 
                      defaultValue={user?.username || 'John Doe'}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-2xl px-6 py-4 text-sm font-bold text-text-primary focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal/30 outline-none transition-all placeholder:opacity-30"
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-text-tertiary uppercase tracking-[0.2em] pl-1">Neural Channel</label>
                    <input 
                      readOnly={!isEditing}
                      type="email" 
                      defaultValue={user?.email || "operator@richguru.io"}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-2xl px-6 py-4 text-sm font-bold text-text-primary focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal/30 outline-none transition-all placeholder:opacity-30"
                    />
                  </div>
                </div>
              </GlassCard>
            </motion.div>

            {/* Broker Bridge Module (OPP-02) */}
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.15 }}>
              <GlassCard variant="elevated" className="p-10 border-white/5">
                <BrokerBridge />
              </GlassCard>
            </motion.div>

            {/* API Access Heuristics */}
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
              <GlassCard variant="glow" className="p-10 border-brand-plum/20">
                <div className="flex justify-between items-center mb-10">
                  <h3 className="text-xs font-black uppercase tracking-[0.2em] text-text-tertiary">API Link Access</h3>
                  <GlassButton variant="ghost" size="sm" className="h-10 rounded-xl text-[9px] font-black uppercase tracking-widest border-brand-plum/30 text-brand-plum-light hover:bg-brand-plum/10">Gen Access Key</GlassButton>
                </div>
                <div className="space-y-4">
                  <div className="p-6 bg-white/[0.02] rounded-2xl border border-white/5 flex justify-between items-center group hover:border-brand-plum/30 hover:bg-white/[0.04] transition-all">
                    <div>
                      <div className="text-[10px] font-black text-text-primary mb-2 uppercase tracking-widest">Main Production Link (v7.1)</div>
                      <div className="text-sm font-mono text-text-tertiary tabular-nums tracking-tighter">sk_live_v7_************************8f2e</div>
                    </div>
                    <button className="p-3 bg-white/5 rounded-xl text-text-tertiary hover:text-brand-plum-light hover:bg-brand-plum/10 transition-all">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                      </svg>
                    </button>
                  </div>
                </div>
              </GlassCard>
            </motion.div>

            {/* Security Audit Trail */}
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
              <GlassCard variant="elevated" className="p-0 overflow-hidden border-white/5">
                <div className="p-10 border-b border-white/5 bg-white/[0.01]">
                  <h3 className="text-xs font-black uppercase tracking-[0.2em] text-text-tertiary">Network Audit Trail</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-bg-primary/40 text-[9px] font-black uppercase tracking-[0.2em] text-text-tertiary border-b border-white/5">
                        <th className="px-10 py-6">Operation</th>
                        <th className="px-10 py-6">Vector Location</th>
                        <th className="px-10 py-6">Protocol</th>
                        <th className="px-10 py-6 text-right">Delta Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5 text-[11px] font-bold">
                      {securityLogs.map((log, i) => (
                        <tr key={i} className="hover:bg-white/[0.03] transition-all group">
                          <td className="px-10 py-6 text-text-primary font-black uppercase tracking-widest group-hover:text-brand-teal-light transition-colors">{log.event}</td>
                          <td className="px-10 py-6 text-text-secondary font-medium tracking-tight">{log.location}</td>
                          <td className="px-10 py-6">
                            <span className="px-3 py-1 rounded-lg bg-brand-teal/10 text-brand-teal-light border border-brand-teal/20 text-[9px] font-black uppercase tracking-widest shadow-glow-sm">
                              {log.status}
                            </span>
                          </td>
                          <td className="px-10 py-6 text-right text-text-tertiary tabular-nums font-mono">{log.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </GlassCard>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
